import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Autocomplete,
  type AutocompleteInputChangeReason,
  Avatar,
  Checkbox,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  debounce,
} from '@mui/material';
import { Building2Icon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { GetMlsNetworkTeamOptionsQuery } from '@graphql/__generated__/graphql';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import {
  AutocompletePaper,
  AutocompleteTextField,
  useFilterAutocompleteOpen,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

const SEARCH_DEBOUNCE_MS = 275;

const GET_MLS_NETWORK_TEAM_OPTIONS = gql(/* GraphQL */ `
  query GetMlsNetworkTeamOptions(
    $countryCode: String!
    $selectedIds: [uuid!]!
    $hasSelectedIds: Boolean!
  ) {
    selected_teams: mls_network_teams(
      where: { country_code: { _eq: $countryCode }, id: { _in: $selectedIds } }
    ) @include(if: $hasSelectedIds) {
      id
      name
      logo_url
    }
    initial_teams: mls_network_teams(
      where: { country_code: { _eq: $countryCode } }
      order_by: [{ name: asc }]
      limit: 20
    ) {
      id
      name
      logo_url
    }
  }
`);

const SEARCH_MLS_NETWORK_TEAMS = gql(/* GraphQL */ `
  query SearchMlsNetworkTeams($countryCode: String!, $search: String!) {
    mls_network_teams(
      where: { country_code: { _eq: $countryCode }, name: { _ilike: $search } }
      order_by: [{ name: asc }]
      limit: 20
    ) {
      id
      name
      logo_url
    }
  }
`);

type TeamOption = GetMlsNetworkTeamOptionsQuery['initial_teams'][number] & {
  id: string;
};

const hasId = (
  t: GetMlsNetworkTeamOptionsQuery['initial_teams'][number],
): t is TeamOption => t.id != null;

interface MultiSelectProps {
  countryCode: string;
  defaultValue: string[];
  onChange: (value: string[]) => void;
  onClose?: () => void;
}

const MlsNetworkTeamMultiSelect = ({
  countryCode,
  defaultValue,
  onChange,
  onClose,
}: MultiSelectProps) => {
  const { t } = useLocale();
  const open = useFilterAutocompleteOpen();
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<TeamOption[]>([]);
  const [options, setOptions] = useState<TeamOption[]>([]);

  const hasSelectedIds = defaultValue.length > 0;
  const { data, loading: defaultLoading } = useQuery(
    GET_MLS_NETWORK_TEAM_OPTIONS,
    {
      variables: {
        countryCode,
        selectedIds: hasSelectedIds ? defaultValue : [],
        hasSelectedIds,
      },
    },
  );

  const [searchTeams, { loading: searchLoading }] = useLazyQuery(
    SEARCH_MLS_NETWORK_TEAMS,
    { notifyOnNetworkStatusChange: true },
  );

  useEffect(() => {
    if (data && options.length === 0 && !inputValue) {
      const rawSelected = (data.selected_teams ?? []).filter(hasId);
      const rawInitial = data.initial_teams.filter(hasId);
      const all = [
        ...rawSelected,
        ...rawInitial.filter(t => !rawSelected.some(s => s.id === t.id)),
      ];
      const selectedIdSet = new Set(defaultValue);
      setSelectedOptions(all.filter(opt => selectedIdSet.has(opt.id)));
      setOptions(all);
    }
  }, [data, options.length, inputValue, defaultValue]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (search: string) => {
        const { data: result } = await searchTeams({
          variables: { countryCode, search: `%${search}%` },
        });
        if (result?.mls_network_teams) {
          setOptions(result.mls_network_teams.filter(hasId));
        } else {
          setOptions([]);
        }
      }, SEARCH_DEBOUNCE_MS),
    [searchTeams, countryCode],
  );

  const handleInputChange = useCallback(
    (
      _: SyntheticEvent,
      newValue: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason !== 'input') {
        return;
      }
      setInputValue(newValue);
      if (newValue) {
        debouncedSearch(newValue);
      } else {
        const rawSelected = (data?.selected_teams ?? []).filter(hasId);
        const rawInitial = (data?.initial_teams ?? []).filter(hasId);
        setOptions([
          ...rawSelected,
          ...rawInitial.filter(t => !rawSelected.some(s => s.id === t.id)),
        ]);
      }
    },
    [debouncedSearch, data],
  );

  const handleChange = useCallback(
    (_: SyntheticEvent, newValue: TeamOption[]) => {
      setSelectedOptions(newValue);
      onChange(newValue.map(v => v.id));
    },
    [onChange],
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      openOnFocus
      open={open}
      disablePortal
      disableClearable
      size="small"
      clearOnBlur={false}
      value={selectedOptions}
      loading={defaultLoading || searchLoading}
      loadingText={t('Loading...')}
      onClose={onClose}
      sx={{ width: 350 }}
      options={options}
      getOptionLabel={opt => opt.name ?? ''}
      filterOptions={x => x}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      onChange={handleChange}
      renderInput={params => (
        <AutocompleteTextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {defaultLoading || searchLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option, { selected }) => (
        <ListItem {...props} key={option.id} dense>
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Checkbox
              edge="start"
              checked={selected}
              tabIndex={-1}
              disableRipple
            />
            <ListItemAvatar>
              <Avatar
                src={option.logo_url ?? undefined}
                variant="rounded"
                sx={{ width: 32, height: 32 }}
              >
                {option.name?.[0]}
              </Avatar>
            </ListItemAvatar>
          </ListItemIcon>
          <ListItemText primary={option.name} />
        </ListItem>
      )}
      noOptionsText={t('No options')}
      slots={{
        paper: AutocompletePaper,
      }}
    />
  );
};

export const MlsNetworkTeamFilter = ({
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  queryParamsScope,
  disabled,
}: QuickFilterProps) => {
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { getValueFromPath } = useSchema();
  const { me } = useAppData();
  const countryCode = me?.tenant?.country_code ?? 'CH';

  const defaultValue = useMemo(
    () => getValueFromPath(path, where)?._in ?? [],
    [getValueFromPath, path, where],
  );

  const handleChange = useCallback(
    (value: string[]) => {
      let newWhere = deleteWhereClause(where, path, true);
      if (value.length > 0) {
        newWhere = addWhereClause(newWhere, [...path, '_in'], value);
      }
      setFiltersParams(newWhere);
    },
    [deleteWhereClause, where, path, setFiltersParams, addWhereClause],
  );

  const handleDelete =
    defaultValue.length > 0 ? () => handleChange([]) : undefined;

  return (
    <FilterChip
      label={label}
      icon={<Building2Icon size={16} />}
      onDelete={handleDelete}
      disabled={disabled?.(where) ?? false}
      renderFilter={({ handleClose }) => (
        <MlsNetworkTeamMultiSelect
          countryCode={countryCode}
          defaultValue={defaultValue}
          onChange={handleChange}
          onClose={handleClose}
        />
      )}
    />
  );
};
